import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { LockOutlined } from '@mui/icons-material';

interface SessionContextType {
  showSessionExpired: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

// Global callback for the API interceptor to trigger session expired modal
let sessionExpiredCallback: (() => void) | null = null;

export function setSessionExpiredCallback(callback: (() => void) | null): void {
  sessionExpiredCallback = callback;
}

export function triggerSessionExpired(): void {
  if (sessionExpiredCallback) {
    sessionExpiredCallback();
  } else {
    // Fallback if context not mounted yet
    window.location.href = '/login';
  }
}

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  const showSessionExpired = useCallback(() => {
    setIsSessionExpired(true);
  }, []);

  // Register the callback when the provider mounts
  useState(() => {
    setSessionExpiredCallback(showSessionExpired);
    return () => setSessionExpiredCallback(null);
  });

  const handleLogin = () => {
    window.location.href = '/login';
  };

  return (
    <SessionContext.Provider value={{ showSessionExpired }}>
      {children}

      {/* Session Expired Modal */}
      <Dialog
        open={isSessionExpired}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#FEF3C7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#D97706',
              }}
            >
              <LockOutlined />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              Session Expired
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Your session has expired due to inactivity. Please log in again to continue.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleLogin}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: '#2563EB',
              '&:hover': {
                backgroundColor: '#1D4ED8',
              },
            }}
          >
            Log In
          </Button>
        </DialogActions>
      </Dialog>
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextType {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
